import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const AnnouncementManagement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [activeTab, setActiveTab] = useState('announcements'); // 'announcements' or 'permissions'
  const [permissions, setPermissions] = useState([]);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'normal',
    start_date: '',
    end_date: '',
    target_audience: [],
    show_as_popup: true,
    popup_frequency: 'once',
    popup_interval_hours: 24,
    expires_at: '',
    is_active: true
  });

  useEffect(() => {
    loadAnnouncements();
    loadPermissions();
    loadUsers();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await api.get('/portal/announcements/management');
      console.log('Announcements API Response:', response);
      console.log('Announcements data:', response.data);
      console.log('Is array?', Array.isArray(response.data));
      console.log('Count:', response.data?.length);

      // Ensure we always set an array
      if (Array.isArray(response.data)) {
        setAnnouncements(response.data);
        console.log('Set announcements:', response.data.length, 'items');
      } else {
        console.warn('Response data is not an array:', typeof response.data, response.data);
        setAnnouncements([]);
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      setAnnouncements([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await api.get('/portal/announcements/permissions');
      // Ensure we always set an array
      setPermissions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading permissions:', error);
      setPermissions([]); // Set empty array on error
    }
  };

  const loadUsers = async () => {
    try {
      // Get ALL users without pagination (limit=9999)
      const response = await api.get('/user-management?limit=9999');
      // The API returns { users: [...], total: X }, so we need to extract the users array
      const usersData = response.data.users || response.data;
      // Ensure we always set an array
      setAllUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error('Error loading users:', error);
      setAllUsers([]); // Set empty array on error
    }
  };

  const handleGrantPermission = async (userId) => {
    try {
      await api.post('/portal/announcements/permissions', { user_id: userId });
      // Reload both permissions and users to keep in sync
      await loadPermissions();
      await loadUsers();
      // Don't close modal - let user grant more if needed
      alert('✅ Permission granted successfully!');
    } catch (error) {
      console.error('Error granting permission:', error);
      alert('❌ Error granting permission: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRevokePermission = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke this permission?')) {
      return;
    }

    try {
      await api.delete(`/portal/announcements/permissions/${userId}`);
      // Reload both permissions and users to keep in sync
      await loadPermissions();
      await loadUsers();
      alert('✅ Permission revoked successfully!');
    } catch (error) {
      console.error('Error revoking permission:', error);
      alert('❌ Error revoking permission: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCreate = () => {
    setEditingAnnouncement(null);
    setFormData({
      title: '',
      content: '',
      priority: 'normal',
      start_date: '',
      end_date: '',
      target_audience: [],
      show_as_popup: true,
      popup_frequency: 'once',
      popup_interval_hours: 24,
      expires_at: '',
      is_active: true
    });
    setShowModal(true);
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title || '',
      content: announcement.content || '',
      priority: announcement.priority || 'normal',
      start_date: announcement.start_date ? announcement.start_date.split('T')[0] : '',
      end_date: announcement.end_date ? announcement.end_date.split('T')[0] : '',
      target_audience: announcement.target_audience || [],
      show_as_popup: announcement.show_as_popup !== false,
      popup_frequency: announcement.popup_frequency || 'once',
      popup_interval_hours: announcement.popup_interval_hours || 24,
      expires_at: announcement.expires_at ? announcement.expires_at.split('T')[0] : '',
      is_active: announcement.is_active !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      await api.delete(`/portal/announcements/${id}`);
      loadAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert('Error deleting announcement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingAnnouncement) {
        await api.put(`/portal/announcements/${editingAnnouncement.id}`, formData);
      } else {
        await api.post('/portal/announcements', formData);
      }
      setShowModal(false);
      loadAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert('Error saving announcement');
    }
  };

  const handleAudienceChange = (role) => {
    const currentAudience = formData.target_audience || [];
    if (currentAudience.includes(role)) {
      setFormData({
        ...formData,
        target_audience: currentAudience.filter(r => r !== role)
      });
    } else {
      setFormData({
        ...formData,
        target_audience: [...currentAudience, role]
      });
    }
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: 'badge-danger',
      high: 'badge-warning',
      normal: 'badge-info',
      low: 'badge-gray'
    };
    return badges[priority] || 'badge-gray';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 theme-text-secondary">Loading announcements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold theme-text-primary">Announcement Management</h1>
        <p className="theme-text-secondary mt-1">Create and manage company announcements</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('announcements')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'announcements'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Announcements
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'permissions'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Permissions
          </button>
        </nav>
      </div>

      {/* Announcements Tab */}
      {activeTab === 'announcements' && (
        <>
          <div className="mb-4 flex justify-end">
            <button
              onClick={handleCreate}
              className="btn-primary flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>New Announcement</span>
            </button>
          </div>

          <div className="card">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="mt-4 theme-text-secondary">Loading announcements...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* Debug Info */}
                <div className="bg-yellow-50 border border-yellow-200 p-2 mb-2 text-xs">
                  <strong>Debug:</strong> Loading: {loading ? 'Yes' : 'No'} |
                  Announcements: {announcements.length} |
                  Type: {Array.isArray(announcements) ? 'Array' : typeof announcements}
                </div>

                <table className="table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Priority</th>
                      <th>Target Audience</th>
                      <th>Status</th>
                      <th>Popup</th>
                      <th>Views</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(announcements) && announcements.length > 0 ? announcements.map((announcement) => (
                <tr key={announcement.id}>
                  <td>
                    <div className="font-medium theme-text-primary">{announcement.title}</div>
                    <div className="text-sm theme-text-tertiary">
                      {announcement.content.substring(0, 60)}...
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${getPriorityBadge(announcement.priority)}`}>
                      {announcement.priority}
                    </span>
                  </td>
                  <td>
                    <div className="text-sm theme-text-secondary">
                      {announcement.target_audience && announcement.target_audience.length > 0
                        ? announcement.target_audience.join(', ')
                        : 'All'}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${announcement.is_active ? 'badge-success' : 'badge-gray'}`}>
                      {announcement.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span className="text-sm">
                      {announcement.show_as_popup ? '✓' : '—'}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center space-x-1">
                      <EyeIcon className="h-4 w-4 theme-text-tertiary" />
                      <span className="text-sm theme-text-secondary">{announcement.view_count || 0}</span>
                    </div>
                  </td>
                  <td className="text-sm theme-text-secondary">
                    {new Date(announcement.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(announcement)}
                        className="p-1 text-blue-600 hover:text-blue-800"
                        title="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="8" className="text-center py-8 theme-text-secondary">
                    No announcements yet. Click "New Announcement" to create your first one.
                  </td>
                </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <>
          <div className="mb-4 flex justify-between items-center">
            <p className="theme-text-secondary">Manage users who can create and manage announcements</p>
            <button
              onClick={() => setShowPermissionModal(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Grant Permission</span>
            </button>
          </div>

          <div className="card">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Granted By</th>
                    <th>Granted At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(permissions) && permissions.length > 0 ? (
                    permissions.map((permission) => (
                      <tr key={permission.user_id}>
                        <td className="font-medium theme-text-primary">
                          {permission.user_name || 'N/A'}
                        </td>
                        <td className="text-sm theme-text-secondary">
                          {permission.email}
                        </td>
                        <td className="text-sm theme-text-secondary">
                          {permission.granted_by_name || 'System'}
                        </td>
                        <td className="text-sm theme-text-secondary">
                          {new Date(permission.granted_at).toLocaleDateString()}
                        </td>
                        <td>
                          <button
                            onClick={() => handleRevokePermission(permission.user_id)}
                            className="p-1 text-red-600 hover:text-red-800"
                            title="Revoke Permission"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-8 theme-text-secondary">
                        No permissions granted yet. Only admins and HR managers can create announcements by default.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Create/Edit Announcement Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-content max-w-2xl">
              <div className="modal-header">
                <h3 className="text-lg font-medium theme-text-primary flex items-center">
                  <MegaphoneIcon className="h-6 w-6 mr-2" />
                  {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
                </h3>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body space-y-4">
                  {/* Title */}
                  <div className="form-group">
                    <label className="block text-sm font-medium theme-text-secondary mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="form-input"
                      placeholder="Enter announcement title"
                    />
                  </div>

                  {/* Content */}
                  <div className="form-group">
                    <label className="block text-sm font-medium theme-text-secondary mb-2">
                      Content *
                    </label>
                    <textarea
                      required
                      rows="4"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="form-textarea"
                      placeholder="Enter announcement content"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Priority */}
                    <div className="form-group">
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="form-select"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>

                    {/* Show as Popup */}
                    <div className="form-group">
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Display Options
                      </label>
                      <div className="flex items-center space-x-4 mt-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.show_as_popup}
                            onChange={(e) => setFormData({ ...formData, show_as_popup: e.target.checked })}
                            className="mr-2"
                          />
                          <span className="text-sm theme-text-secondary">Show as Popup</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="mr-2"
                          />
                          <span className="text-sm theme-text-secondary">Active</span>
                        </label>
                      </div>
                    </div>

                    {/* Popup Frequency */}
                    {formData.show_as_popup && (
                      <div className="form-group">
                        <label className="block text-sm font-medium theme-text-secondary mb-2">
                          Popup Frequency
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <select
                              value={formData.popup_frequency}
                              onChange={(e) => setFormData({ ...formData, popup_frequency: e.target.value })}
                              className="form-select"
                            >
                              <option value="once">Show Once Only</option>
                              <option value="every_login">Every Login</option>
                              <option value="interval">Every X Hours</option>
                            </select>
                            <p className="text-xs theme-text-tertiary mt-1">
                              {formData.popup_frequency === 'once' && '⏱️ Shows only once per user'}
                              {formData.popup_frequency === 'every_login' && '🔄 Shows every time user logs in'}
                              {formData.popup_frequency === 'interval' && '⏰ Shows after specified hours'}
                            </p>
                          </div>
                          {formData.popup_frequency === 'interval' && (
                            <div>
                              <input
                                type="number"
                                min="1"
                                value={formData.popup_interval_hours}
                                onChange={(e) => setFormData({ ...formData, popup_interval_hours: parseInt(e.target.value) || 24 })}
                                className="form-input"
                                placeholder="Hours"
                              />
                              <p className="text-xs theme-text-tertiary mt-1">
                                Popup interval in hours
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    {/* Start Date */}
                    <div className="form-group">
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        className="form-input"
                      />
                    </div>

                    {/* End Date */}
                    <div className="form-group">
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        className="form-input"
                      />
                    </div>

                    {/* Expires At */}
                    <div className="form-group">
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Popup Expires
                      </label>
                      <input
                        type="date"
                        value={formData.expires_at}
                        onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Target Audience */}
                  <div className="form-group">
                    <label className="block text-sm font-medium theme-text-secondary mb-2">
                      Target Audience
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {['all', 'admin', 'hr_manager', 'manager', 'employee', 'accountant'].map((role) => (
                        <label key={role} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.target_audience.includes(role)}
                            onChange={() => handleAudienceChange(role)}
                            className="mr-2"
                          />
                          <span className="text-sm theme-text-secondary capitalize">{role.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingAnnouncement ? 'Update' : 'Create'} Announcement
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Grant Permission Modal */}
      {showPermissionModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-content max-w-2xl">
              <div className="modal-header flex justify-between items-center">
                <h3 className="text-lg font-medium theme-text-primary">Grant Announcement Permission</h3>
                <button
                  onClick={loadUsers}
                  className="text-sm px-3 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                  title="Refresh user list"
                >
                  🔄 Refresh
                </button>
              </div>

              <div className="modal-body">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️ Note:</strong> Admin and HR Manager users have announcement permissions by default.
                    You can grant permissions to other users below.
                  </p>
                </div>

                <p className="text-sm theme-text-secondary mb-3">
                  <strong>Total Users:</strong> {allUsers.length} |
                  <strong className="ml-2">With Permissions:</strong> {permissions.length}
                </p>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {Array.isArray(allUsers) && allUsers.length > 0 ? (
                    <>
                      {/* Users who already have permission (show as disabled) */}
                      {allUsers
                        .filter(user =>
                          (Array.isArray(permissions) && permissions.some(p => p.user_id === user.id)) ||
                          user.role === 'admin' ||
                          user.role === 'hr_manager'
                        )
                        .map((user) => {
                          const hasExplicitPermission = Array.isArray(permissions) && permissions.some(p => p.user_id === user.id);
                          const hasRolePermission = user.role === 'admin' || user.role === 'hr_manager';

                          return (
                            <div
                              key={user.id}
                              className="w-full text-left p-3 rounded-lg border border-gray-200 bg-gray-50 opacity-60"
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="font-medium text-gray-700">
                                    {user.first_name && user.last_name
                                      ? `${user.first_name} ${user.last_name}`
                                      : user.email
                                    }
                                  </div>
                                  <div className="text-sm text-gray-600">{user.email}</div>
                                  <div className="text-xs text-gray-500 capitalize mt-1">
                                    {user.role}
                                  </div>
                                </div>
                                <div className="ml-2">
                                  {hasRolePermission && (
                                    <span className="inline-block px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                      ✓ Default Access
                                    </span>
                                  )}
                                  {hasExplicitPermission && !hasRolePermission && (
                                    <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                      ✓ Has Permission
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      }

                      {/* Divider if there are both groups */}
                      {allUsers.some(user =>
                        !permissions.some(p => p.user_id === user.id) &&
                        user.role !== 'admin' &&
                        user.role !== 'hr_manager'
                      ) && allUsers.some(user =>
                        permissions.some(p => p.user_id === user.id) ||
                        user.role === 'admin' ||
                        user.role === 'hr_manager'
                      ) && (
                        <div className="border-t border-gray-300 my-3 pt-3">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Available Users
                          </p>
                        </div>
                      )}

                      {/* Users who can be granted permission */}
                      {allUsers
                        .filter(user =>
                          (!Array.isArray(permissions) || !permissions.some(p => p.user_id === user.id)) &&
                          user.role !== 'admin' &&
                          user.role !== 'hr_manager'
                        )
                        .map((user) => (
                          <button
                            key={user.id}
                            onClick={() => handleGrantPermission(user.id)}
                            className="w-full text-left p-3 rounded-lg border theme-border-primary hover:bg-blue-50 hover:border-blue-300 transition-colors"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-medium theme-text-primary">
                                  {user.first_name && user.last_name
                                    ? `${user.first_name} ${user.last_name}`
                                    : user.email
                                  }
                                </div>
                                <div className="text-sm theme-text-secondary">{user.email}</div>
                                <div className="text-xs theme-text-tertiary capitalize mt-1">
                                  {user.role} {user.is_active === false && '(Inactive)'}
                                </div>
                              </div>
                              <div className="ml-2 text-blue-600">
                                <span className="text-xs">Click to grant →</span>
                              </div>
                            </div>
                          </button>
                        ))
                      }

                      {/* No available users message */}
                      {allUsers.filter(user =>
                        (!Array.isArray(permissions) || !permissions.some(p => p.user_id === user.id)) &&
                        user.role !== 'admin' &&
                        user.role !== 'hr_manager'
                      ).length === 0 && (
                        <div className="text-center py-8 theme-text-secondary">
                          <p className="font-medium">All users already have permissions!</p>
                          <p className="text-xs mt-2">Either through their role or explicit grants.</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 theme-text-secondary">
                      <p className="font-medium">No users found</p>
                      <p className="text-xs mt-2">Click the Refresh button above to reload users.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowPermissionModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementManagement;
